import { useEffect, useMemo, useState } from 'react'
import Image from 'next/image'
import { useRouter } from 'next/router'

import OrderSummary from 'ui/order/OrderSummary'

import { getAllAvailableAddOns } from '@/lib/utils/addOns'

import { useCartContext } from '@/context/CartContext'
import { Loading, PrismicLink } from 'ui'
import { useCart } from 'hooks/graphql/legacy-api'
import CartTotal from '@/components/cart/cartSummary/CartTotal'
import CartReviewPanel from '@/components/cart/cartSummary/CartReviewPanel'
import { useUpdateCartItem } from '@/lib/graphql/realthread/mutations'
import OrderOptionsCards from '@/components/order/options/OrderOptionsCards'
import clsx from 'clsx'

export default function CartSummary({ globalBuilderData }) {
	const { cart, refetch, loading } = useCart()
	const router = useRouter()
	const { cartItemsQuantities } = useCartContext()

	const { updateCartItem } = useUpdateCartItem()

	const [isFoldPolyInCartItems, setIsFoldPolyInCartItems] = useState(true)
	const [goToCheckoutLoading, setGoToCheckoutLoading] = useState(false)

	const foldPolyInfo = useMemo(() => {
		if (cart) {
			const cartTotalQuantity = cart.items.reduce((acc, item) => {
				return acc + item.quantity
			}, 0)

			const allAvailableAddOns = getAllAvailableAddOns([], cartTotalQuantity)

			return allAvailableAddOns.find((addOn) => addOn.name === 'foldPoly')
		}
	}, [cart])

	// Folding & bagging option will appear in the cart summary if none of the items in the cart
	// has this option previoulsy selected
	useEffect(() => {
		if (cart?.items) {
			if (cart.items.find((item) => item.foldPoly)) {
				setIsFoldPolyInCartItems(true)
			} else {
				setIsFoldPolyInCartItems(false)
			}
		}
	}, [cart])

	const goToCheckout = async () => {
		setGoToCheckoutLoading(true)
		await refetch()
		await router.push('/checkout')
		setGoToCheckoutLoading(false)
	}

	const handleAddToCart = async () => {
		setIsFoldPolyInCartItems(!isFoldPolyInCartItems)
		const promises = []
		cart?.items?.forEach(async (item) => {
			const products =
				cartItemsQuantities.find((cartItem) => cartItem.token === item.itemStyles[0].token)
					?.products ??
				item.itemStyles[0].products.map((product) => {
					return {
						sfid: product.sfid,
						quantity: product.quantity,
						sizeId: product.sizeId
					}
				})

			promises.push(
				updateCartItem({
					variables: {
						token: item.itemStyles[0].token,
						input: {
							colorSfid: item.itemStyles[0].color.sfid,
							styleSfid: item.itemStyles[0].style.sfid,
							foil: item.foil,
							foldPoly: isFoldPolyInCartItems ? false : true,
							hemTags: item.hemTags,
							tagPrinting: item.tagPrinting,
							inkType: item.inkType,
							notesFront: item.notesFront,
							name: item.name ?? 'My design #1',
							decorationMethod: item.decorationMethod,
							canvasState: item.canvasState || null,
							daysRush: item.daysRush,
							numColorsBack: item.numColorsBack,
							numColorsFront: item.numColorsFront,
							numColorsSleeveLeft: item.numColorsSleeveLeft,
							numColorsSleeveRight: item.numColorsSleeveRight,
							products,
							shippingSpeed: item.shippingSpeed
						}
					}
				})
			)
		})

		await Promise.all(promises)

		await refetch()
	}

	return (
		<OrderSummary>
			<>
				{loading ? (
					<Loading />
				) : cart?.items.length > 0 ? (
					<>
						<h1 className="font-serif text-[32px] pb-8">Cart</h1>

						<CartReviewPanel />
						{/* Add Folding & Bagging Button */}
						<button
							onClick={handleAddToCart}
							className={clsx(
								'mt-auto transition duration-300 ease-out delay-500 opacity-0 -translate-y-10',
								isFoldPolyInCartItems
									? 'opacity-0 -translate-y-10 pointer-events-none'
									: 'opacity-100 translate-y-0'
							)}
						>
							<OrderOptionsCards
								process={{
									id: foldPolyInfo.key,
									imageUrl: foldPolyInfo.imageUrl,
									title: foldPolyInfo.customizedTitle,
									description: foldPolyInfo.description,
									name: foldPolyInfo.name
								}}
								checked={isFoldPolyInCartItems}
								image={globalBuilderData?.data?.fold_and_bag_image}
							/>
						</button>

						{/* Order total */}
						<CartTotal />

						{/* Proceed to checkout */}
						<button
							className="button button-dark w-full"
							onClick={() => goToCheckout()}
							disabled={goToCheckoutLoading}
						>
							Proceed to checkout
						</button>
						<div className="flex flex-col gap-1 text-center text-sm font-medium mt-6 pb-6">
							<span>You will not be charged until you approve your proof.</span>
							{globalBuilderData?.data?.cart && (
								<PrismicLink
									link={globalBuilderData.data.cart}
									label="Learn about our order process"
									className="hyperlink mx-auto"
								/>
							)}
						</div>
					</>
				) : (
					<div className="flex flex-col h-full">
						<h1 className="font-serif text-[32px] pb-8">Cart</h1>

						<div className="py-6 rounded-lg border text-center text-lg font-medium font-serif bg-white">
							<h5>No products in your cart.</h5>
						</div>

						<span className="mt-auto text-center font-serif mb-6 pt-16 text-lg">
							Perks of every order
						</span>
						<div className="grid grid-cols-3 gap-4 text-center text-[13px] font-medium leading-[13px]">
							<div className="col-span-1 flex flex-col gap-2 items-center">
								<Image
									src="/images/svg/shirts.svg"
									alt="Shirts"
									width={60}
									height={60}
									quality={70}
									sizes="60px"
									className="mx-auto"
								/>
								<span>Low minimums</span>
							</div>
							<div className="col-span-1 flex flex-col gap-2 items-center">
								<Image
									src="/images/svg/star.svg"
									alt="Shirts"
									width={60}
									height={60}
									quality={70}
									sizes="60px"
									className="mx-auto"
								/>
								<span>Free standard shipping</span>
							</div>
							<div className="col-span-1 flex flex-col gap-2 items-center">
								<Image
									src="/images/svg/hearts.svg"
									alt="Shirts"
									width={60}
									height={60}
									className="mx-auto"
									quality={70}
									sizes="60px"
								/>
								<span>Money back guarantee</span>
							</div>
						</div>
					</div>
				)}
			</>
		</OrderSummary>
	)
}
